import {
  GroupDropdown,
  MY_ACCOUNT_LIST,
  OrgFilter,
  OrgReload,
  OrgSearch,
  sortTableData,
  useAppDispatch,
  useAppSelector
} from '@monorepo/shared';
import { OrgGroupSearch } from '@monorepo/shared/components/OrgSearch';
import { selectAssets } from '@monorepo/shared/store/assetSlice';
import { selectInstances } from '@monorepo/shared/store/instanceSlice';
import { selectInstancesExtraInfo } from '@monorepo/shared/store/instanceExtraSlice';
import { NavItemData } from '@monorepo/shared/store/sidemenuSlice';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ButtonToolbar,
  Divider,
  Stack,
  Tooltip,
  Whisper
} from 'rsuite';

interface IProps {
  sortColumn: string;
  sortType: string;
  reloadData: any;
  tableData: Record<any, any>[];
  setSortData: any;
  currentGroup: Record<string, any>;
  setGroupSearchKeyValue: any;
}

export const OrgToolbar = ({
  reloadData,
  tableData,
  sortColumn,
  sortType,
  setSortData,
  currentGroup,
  setGroupSearchKeyValue
}: IProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [activeMenu, setActiveMenu] = useState<string>('account');
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [accountAlert, setAccountAlert] = useState<Record<string, any>[]>([]);
  const [filters, setFilters] = useState<string[]>([]);
  const [accountExpiring, setAccountExpiring] = useState<Record<string, any>[]>(
    []
  );
  const [accountUpgrade, setAccountUpgrade] = useState<Record<string, any>[]>(
    []
  );
  const instances = useAppSelector(selectInstances);
  const instancesExtraInfo = useAppSelector(selectInstancesExtraInfo);
  const assets = useAppSelector(selectAssets);

  const OrgNavs = [
    {
      eventKey: 'account',
      isDefault: true,
      myIcon: {
        name: 'myAccountIcon',
        family: 'fortinetone',
        color: '#212121',
        size: '22px',
        left: '14px',
        badgeText: '0'
      },
      title: t('common.orgToolbar.accounts'),
      onClick: () => {
        setActiveMenu('ou');
      }
    },
    {
      eventKey: 'alarm',
      myIcon: {
        name: 'alert',
        family: 'fontello',
        color: '#212121',
        size: '22px',
        left: '14px',
        badgeText: '0'
      },
      title: t('common.orgToolbar.alarms'),
      onClick: () => {
        setActiveMenu('alarm');
      }
    },
    {
      eventKey: 'expiring',
      myIcon: {
        name: 'expiring',
        family: 'fontello',
        color: '#212121',
        size: '22px',
        left: '14px',
        badgeText: '0'
      },
      title: t('common.orgToolbar.expiring'),
      onClick: () => {
        setActiveMenu('expiring');
      }
    },
    {
      eventKey: 'upgrade',
      myIcon: {
        name: 'upgradeIcon',
        family: 'fortinetone',
        color: '#212121',
        size: '22px',
        left: '14px',
        badgeText: '0'
      },
      title: t('common.orgToolbar.upgrade'),
      onClick: () => {
        setActiveMenu('upgrade');
      }
    }
  ];
  useEffect(() => {
    const alerts = tableData.filter((account: Record<string, any>) => {
      const data = instances?.[currentGroup.id]?.[account?.account_id];
      const licenseAlerts = data?.detail?.license || {};
      return licenseAlerts.hasEntitleAlert || licenseAlerts.isExpired;
    });
    const expiringAccounts = tableData.filter(
      (account: Record<string, any>) => {
        const data = instances?.[currentGroup.id]?.[account?.account_id];
        const licenseAlerts = data?.detail?.license || {};
        return licenseAlerts.isExpiring;
      }
    );
    const upgradeAccounts = tableData.filter((account: Record<string, any>) => {
      const data = instancesExtraInfo?.[currentGroup.id]?.[account?.account_id];
      return data?.firmware_download;
    });
    setAccountUpgrade(upgradeAccounts);
    setAccountAlert(alerts);
    setAccountExpiring(expiringAccounts);
    const getSourceData = () => {
      return activeMenu === 'alarm'
        ? accountAlert
        : activeMenu === 'expiring'
        ? accountExpiring
        : activeMenu === 'upgrade'
        ? accountUpgrade
        : tableData;
    };
    const sortData = sortTableData({
      sortColumn,
      sortType,
      searchKeyword,
      filters,
      instances: instances?.[currentGroup.id],
      assets: assets?.[currentGroup.id],
      sourceData: getSourceData()
    });
    setSortData([...sortData]);
  }, [
    instances,
    instancesExtraInfo,
    assets,
    tableData,
    activeMenu,
    sortColumn,
    sortType,
    searchKeyword,
    filters,
    currentGroup,
    t
  ]);

  useEffect(() => {
    const newMenu = [...OrgNavs].map((menu: NavItemData) => {
      let obj = { ...menu };
      if (menu.eventKey === 'account' && menu.myIcon) {
        obj = {
          ...menu,
          myIcon: { ...menu.myIcon, badgeText: tableData.length + '' }
        };
      }
      if (menu.eventKey === 'alarm' && menu.myIcon) {
        obj = {
          ...menu,
          myIcon: { ...menu.myIcon, badgeText: accountAlert.length + '' }
        };
      }
      if (menu.eventKey === 'expiring' && menu.myIcon) {
        obj = {
          ...menu,
          myIcon: { ...menu.myIcon, badgeText: accountExpiring.length + '' }
        };
      }
      if (menu.eventKey === 'upgrade' && menu.myIcon) {
        obj = {
          ...menu,
          myIcon: { ...menu.myIcon, badgeText: accountUpgrade.length + '' }
        };
      }
      return obj;
    });
    setMenus(newMenu);
  }, [tableData, accountAlert, accountUpgrade, accountExpiring]);

  const [menus, setMenus] = useState<Record<string, any>[]>(OrgNavs);
  return (
    <Stack justifyContent="space-between" style={{ flexWrap: 'wrap' }}>
      <Stack.Item>
        {currentGroup?.id === MY_ACCOUNT_LIST ? (
          <ButtonToolbar>
            {menus.map((obj: Record<string, any>, index: number) => (
              <div key={index}>
                <Whisper
                  placement="top"
                  controlId="control-id-hover"
                  trigger="hover"
                  speaker={<Tooltip>{obj?.title}</Tooltip>}
                >
                  <Button onClick={obj?.onClick} appearance="subtle">
                    <Stack
                      spacing={8}
                      style={{
                        fontSize: obj?.myIcon.size,
                        color: obj?.myIcon.color
                      }}
                    >
                      <i
                        className={`icon-${obj?.myIcon.family}-${obj?.myIcon.name} `}
                      />
                      <span>{obj?.myIcon.badgeText}</span>
                    </Stack>
                  </Button>
                </Whisper>
                <Divider vertical />
              </div>
            ))}
          </ButtonToolbar>
        ) : (
          <></>
        )}
      </Stack.Item>
      <Stack.Item grow={1}>
        <Stack
          className="table-toolbar"
          justifyContent="flex-end"
          spacing={18}
          style={{ flexWrap: 'wrap' }}
        >
          <ButtonToolbar>
            {currentGroup?.id === MY_ACCOUNT_LIST ? (
              <>
                <OrgFilter onChange={setFilters} type="faz" />
                <OrgReload onReload={reloadData} />
              </>
            ) : (
              <></>
            )}
            {/* <OrgReload onReload={reloadData} /> */}
          </ButtonToolbar>
          {currentGroup?.id === MY_ACCOUNT_LIST ? (
            <OrgSearch
              searchKeyword={searchKeyword}
              setSearchKeyword={setSearchKeyword}
            />
          ) : (
            <OrgGroupSearch
              setGroupSearchKeyValue={setGroupSearchKeyValue}
              currentGroup={currentGroup}
            />
          )}
          <GroupDropdown />
        </Stack>
      </Stack.Item>
    </Stack>
  );
};
